import { NgIf } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Chart, ChartConfiguration, Plugin } from 'chart.js';
import { MatrixController, MatrixElement } from 'chartjs-chart-matrix';
import { WordCloudController, WordElement } from 'chartjs-chart-wordcloud';
import { NgChartsModule } from 'ng2-charts';
import { ChartWidget } from '../../../../models/preview/preview.models';
import { prepareForDrawing } from '../../../../services/report-preview-chart-configs';

Chart.register(
	MatrixController,
	MatrixElement,
	WordCloudController,
	WordElement
);

interface SegmentLabelsOptions {
	display?: boolean;
	color?: string;
	fontFamily?: string;
	fontSize?: number;
	fontWeight?: string;
	minPercentageToShow?: number;
}

interface PolarAreaLabelsOptions {
	display?: boolean;
	color?: string;
	fontFamily?: string;
	fontSize?: number;
	fontWeight?: string;
	labelRadiusOffset?: number;
	spokeColor?: string;
	spokeWidth?: number;
}

interface BarValueLabelsOptions {
	display?: boolean;
	color?: string;
	fontFamily?: string;
	fontSize?: number;
	fontWeight?: string;
	format?: 'plain' | 'compact';
}

function formatCompactValue(value: number): string {
	const absoluteValue = Math.abs(value);

	if (absoluteValue >= 1000000000) {
		return `${(value / 1000000000).toFixed(
			absoluteValue >= 10000000000 ? 0 : 1
		)}B`;
	}

	if (absoluteValue >= 1000000) {
		return `${(value / 1000000).toFixed(
			absoluteValue >= 10000000 ? 0 : 1
		)}M`;
	}

	if (absoluteValue >= 1000) {
		return `${(value / 1000).toFixed(absoluteValue >= 10000 ? 0 : 1)}K`;
	}

	return `${Math.round(value)}`;
}

const segmentLabelsPlugin: Plugin = {
	id: 'segmentLabels',
	afterDatasetsDraw(chart) {
		const options = (
			chart.options.plugins as Record<string, unknown> | undefined
		)?.['segmentLabels'] as SegmentLabelsOptions | undefined;

		if (!options?.display) {
			return;
		}

		const dataset = chart.data.datasets[0];
		const meta = chart.getDatasetMeta(0);
		const data = (dataset?.data ?? []) as Array<
			number | string | null | undefined
		>;
		const total = data.reduce<number>(
			(sum, value) => sum + (Number(value) || 0),
			0
		);

		if (!meta?.data?.length || total <= 0) {
			return;
		}

		const color = options.color ?? '#FFFFFF';
		const fontSize = options.fontSize ?? 20;
		const fontWeight = options.fontWeight ?? '700';
		const fontFamily = options.fontFamily ?? 'Gilroy';
		const minPercentageToShow = options.minPercentageToShow ?? 1;
		const context = chart.ctx;

		context.save();
		context.fillStyle = color;
		context.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
		context.textAlign = 'center';
		context.textBaseline = 'middle';

		meta.data.forEach((element, index) => {
			const rawValue = Number(data[index]) || 0;
			const percentage = (rawValue / total) * 100;

			if (percentage < minPercentageToShow) {
				return;
			}

			const { x, y } = element.tooltipPosition(true);
			if (x == null || y == null) {
				return;
			}
			context.fillText(`${Math.round(percentage)}%`, x, y);
		});

		context.restore();
	}
};

const polarAreaLabelsPlugin: Plugin<'polarArea'> = {
	id: 'polarAreaLabels',
	beforeDatasetsDraw(chart) {
		const options = (
			chart.options.plugins as Record<string, unknown> | undefined
		)?.['polarAreaLabels'] as PolarAreaLabelsOptions | undefined;

		if (!options?.display) {
			return;
		}

		const scale = (chart as any).scales?.['r'];
		if (!scale) return;

		const ctx = chart.ctx;
		const cx: number = scale.xCenter;
		const cy: number = scale.yCenter;
		const outerRadius: number = scale.drawingArea;
		const labelsCount = chart.data.labels?.length ?? 0;

		if (!labelsCount) return;

		const segmentAngle = (2 * Math.PI) / labelsCount;

		ctx.save();
		ctx.strokeStyle = options.spokeColor ?? '#E8EDF5';
		ctx.lineWidth = options.spokeWidth ?? 1.5;

		for (let i = 0; i < labelsCount; i++) {
			const angle = -Math.PI / 2 - segmentAngle / 2 + segmentAngle * i;
			ctx.beginPath();
			ctx.moveTo(cx, cy);
			ctx.lineTo(
				cx + Math.cos(angle) * outerRadius,
				cy + Math.sin(angle) * outerRadius
			);
			ctx.stroke();
		}

		ctx.restore();
	},
	afterDraw(chart) {
		const options = (
			chart.options.plugins as Record<string, unknown> | undefined
		)?.['polarAreaLabels'] as PolarAreaLabelsOptions | undefined;

		if (!options?.display) {
			return;
		}

		const scale = (chart as any).scales?.['r'];
		if (!scale) return;

		const labels = (chart.data.labels ?? []) as string[];
		const labelsCount = labels.length;
		if (!labelsCount) return;

		const ctx = chart.ctx;
		const cx: number = scale.xCenter;
		const cy: number = scale.yCenter;
		const labelRadius: number =
			scale.drawingArea + (options.labelRadiusOffset ?? 22);
		const segmentAngle = (2 * Math.PI) / labelsCount;

		ctx.save();
		ctx.font = `${options.fontWeight ?? '700'} ${
			options.fontSize ?? 12
		}px ${options.fontFamily ?? 'Inter, sans-serif'}`;
		ctx.textAlign = 'center';
		ctx.textBaseline = 'middle';
		ctx.fillStyle = options.color ?? '#000000';

		labels.forEach((label, index) => {
			const midAngle = -Math.PI / 2 + segmentAngle * index;
			const x = cx + Math.cos(midAngle) * labelRadius;
			const y = cy + Math.sin(midAngle) * labelRadius;

			ctx.save();
			ctx.translate(x, y);
			let rotation = midAngle + Math.PI / 2;
			if (Math.sin(midAngle) > 0) {
				rotation += Math.PI;
			}
			ctx.rotate(rotation);
			ctx.fillText(label, 0, 0);
			ctx.restore();
		});

		ctx.restore();
	}
};

const barValueLabelsPlugin: Plugin<'bar'> = {
	id: 'barValueLabels',
	afterDatasetsDraw(chart) {
		const options = (
			chart.options.plugins as Record<string, unknown> | undefined
		)?.['barValueLabels'] as BarValueLabelsOptions | undefined;

		if (!options?.display) {
			return;
		}

		const dataset = chart.data.datasets[0];
		const meta = chart.getDatasetMeta(0);
		const data = (dataset?.data ?? []) as Array<
			number | string | null | undefined
		>;

		if (!meta?.data?.length || !data.length) {
			return;
		}

		const ctx = chart.ctx;
		ctx.save();
		ctx.fillStyle = options.color ?? '#0F121A';
		ctx.font = `${options.fontWeight ?? '700'} ${
			options.fontSize ?? 11
		}px ${options.fontFamily ?? 'Gilroy'}`;
		ctx.textAlign = 'center';
		ctx.textBaseline = 'bottom';

		meta.data.forEach((element, index) => {
			const rawValue = Number(data[index]) || 0;
			const { x, y } = element.tooltipPosition(true);
			if (x == null || y == null) {
				return;
			}
			const label =
				options.format === 'compact'
					? formatCompactValue(rawValue)
					: rawValue.toLocaleString('en-US');

			ctx.fillText(label, x, y - 6);
		});

		ctx.restore();
	}
};

const wordCloudColorPlugin: Plugin = {
	id: 'wordCloudColor',
	beforeDatasetsDraw(chart) {
		const meta = chart.getDatasetMeta(0) as unknown as {
			type?: string;
			controller?: { constructor?: { id?: string } };
			data?: Array<{ options?: Record<string, unknown> }>;
		};
		const controllerType = meta.controller?.constructor?.id;
		const metaType = meta.type;
		const configType = (chart.config as { type?: string } | undefined)
			?.type;

		if (
			configType !== 'wordCloud' &&
			controllerType !== 'wordCloud' &&
			metaType !== 'wordCloud'
		) {
			return;
		}

		const dataset = chart.data.datasets[0] as unknown as
			| Record<string, unknown>
			| undefined;
		const chartOptions = chart.options as Record<string, unknown>;
		const color =
			resolveStringColor(dataset?.['color']) ??
			resolveStringColor(chartOptions['color']) ??
			'#2872E1';
		const hoverColor =
			resolveStringColor(dataset?.['hoverColor']) ??
			resolveStringColor(chartOptions['hoverColor']) ??
			color;

		if (dataset) {
			dataset['color'] = color;
			dataset['hoverColor'] = hoverColor;
		}

		chartOptions['color'] = color;
		chartOptions['hoverColor'] = hoverColor;

		meta.data?.forEach(element => {
			const options = element.options;
			if (!options) {
				return;
			}

			options['color'] = color;
			options['hoverColor'] = hoverColor;
		});
	}
};

function resolveStringColor(value: unknown): string | null {
	return typeof value === 'string' && value.trim() ? value : null;
}

interface DayMarksOptions {
	marks?: Array<{ index: number; number: string }>;
}

/** Radius of a day's mark, in canvas pixels. */
const DAY_MARK_RADIUS = 9;

/**
 * A numbered disc on each day a trend marks (`blockDayMarks`), in the line's
 * own colour, at that day's point: the number is the one its line under the
 * chart carries. Drawn on the canvas rather than beside it so the editor, the
 * PDF, the shared page and present mode place it identically.
 */
const dayMarksPlugin: Plugin = {
	id: 'reportDayMarks',
	afterDatasetsDraw(chart) {
		const options = (
			chart.options.plugins as Record<string, unknown> | undefined
		)?.['reportDayMarks'] as DayMarksOptions | undefined;
		const marks = options?.marks ?? [];
		const meta = chart.getDatasetMeta(0);
		const area = chart.chartArea;
		if (!marks.length || !meta?.data?.length || !area) {
			return;
		}

		const dataset = chart.data.datasets[0] as unknown as
			| Record<string, unknown>
			| undefined;
		const color = resolveStringColor(dataset?.['borderColor']) ?? '#2872E1';
		// CONTRACT: the report's theme tokens (ReportThemeService), which the
		// canvas inherits from the report surface it is drawn on.
		const style = getComputedStyle(chart.canvas);
		const surface =
			style.getPropertyValue('--report-chart-surface').trim() ||
			'#FFFFFF';
		const font =
			style.getPropertyValue('--report-chart-font').trim() ||
			'Inter, sans-serif';
		const ctx = chart.ctx;

		ctx.save();
		ctx.font = `700 11px ${font}`;
		ctx.textAlign = 'center';
		ctx.textBaseline = 'middle';
		for (const mark of marks) {
			const element = meta.data[mark.index];
			if (!element) {
				continue;
			}
			const { x, y } = element.tooltipPosition(true);
			if (x == null || y == null) {
				continue;
			}
			const top = area.top + DAY_MARK_RADIUS;
			const cy = Math.min(
				Math.max(y, top),
				area.bottom - DAY_MARK_RADIUS
			);
			ctx.beginPath();
			ctx.arc(x, cy, DAY_MARK_RADIUS, 0, Math.PI * 2);
			ctx.fillStyle = color;
			ctx.fill();
			ctx.lineWidth = 2;
			ctx.strokeStyle = surface;
			ctx.stroke();
			ctx.fillStyle = surface;
			ctx.fillText(mark.number, x, cy + 0.5);
		}
		ctx.restore();
	}
};

@Component({
	standalone: true,
	selector: 'app-chart-widget',
	templateUrl: './chart-widget.component.html',
	styleUrls: ['./chart-widget.component.scss'],
	imports: [NgIf, NgChartsModule]
})
export class ChartWidgetComponent {
	private _widget!: ChartWidget;
	private lastChartAreaKey = '';

	@Output() chartAreaChange = new EventEmitter<{
		left: number;
		right: number;
		totalWidth: number;
	}>();

	@Input() set widget(value: ChartWidget) {
		this._widget = value;
		const config = value.content
			.chartJsConfig as unknown as ChartConfiguration;
		prepareForDrawing(config);
		if ((window as any).reportExportMode) {
			config.options = config.options ?? {};
			(config.options as Record<string, unknown>)['animation'] = false;
			(config.options as Record<string, unknown>)['animations'] = false;
		}
		this.config = config;
	}

	get widget(): ChartWidget {
		return this._widget;
	}

	config?: ChartConfiguration | Record<string, unknown>;
	readonly sharedPlugins: Plugin[] = [
		segmentLabelsPlugin,
		polarAreaLabelsPlugin as unknown as Plugin,
		barValueLabelsPlugin as unknown as Plugin,
		wordCloudColorPlugin as unknown as Plugin,
		dayMarksPlugin
	];

	readonly chartAreaReporterPlugin: Plugin = {
		id: 'chartAreaReporter',
		afterLayout: chart => {
			if (!chart.chartArea) {
				return;
			}

			const left = Math.round(chart.chartArea.left);
			const right = Math.round(chart.chartArea.right);
			const totalWidth = Math.round(chart.width);
			const nextChartAreaKey = `${left}:${right}:${totalWidth}`;
			if (nextChartAreaKey === this.lastChartAreaKey) {
				return;
			}

			this.lastChartAreaKey = nextChartAreaKey;
			this.chartAreaChange.emit({
				left,
				right,
				totalWidth
			});
		}
	};

	readonly plugins: Plugin[] = [
		...this.sharedPlugins,
		this.chartAreaReporterPlugin
	];

	get chartType(): string {
		return ((this.config as ChartConfiguration | undefined)?.type ??
			'bar') as string;
	}

	get isPieChart(): boolean {
		return this.chartType === 'pie' || this.chartType === 'doughnut';
	}

	get chartData(): ChartConfiguration['data'] | undefined {
		return (this.config as ChartConfiguration | undefined)?.data;
	}

	get chartOptions(): ChartConfiguration['options'] | undefined {
		return (this.config as ChartConfiguration | undefined)?.options;
	}
}
